import { useCallback, useMemo, useState } from 'react';
import { Linking, Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { useFileStore, useToastStore, useChatInputStore } from '@/store';
import { getTemporaryFileId } from '@/constants/utils/files';
import useTranslation from '@/hooks/useTranslation';
import {
  describeFileLimitViolation,
  getFileValidationLimits,
  probeMediaDurationSeconds,
} from '@/constants/utils/file_upload_limits';

// File types that can come from the device camera / photo library on
// native. Audio is intentionally excluded since the native pickers don't
// expose mic capture; audio params fall back to Files for now.
const PHOTO_LIBRARY_FILE_TYPES = new Set(['image', 'video']);

const isPhotoLibraryFileType = (fileType) => PHOTO_LIBRARY_FILE_TYPES.has(fileType);

const fileTypeToImagePickerMediaTypes = (fileType) => {
  if (fileType === 'image') return ['images'];
  if (fileType === 'video') return ['videos'];
  // 'any' or other types — let the picker show everything it can.
  return ['images', 'videos'];
};

/**
 * Builds an HTML `accept="…"` string from the file param's validation schema.
 */
export const buildAcceptString = (param) => {
  const fileVal = param?.validation?.file || {};
  const mimes = Array.isArray(fileVal.acceptedMimeTypes) ? fileVal.acceptedMimeTypes : [];
  const exts = Array.isArray(fileVal.acceptedExtensions) ? fileVal.acceptedExtensions : [];
  const all = [...mimes, ...exts];
  return all.length > 0 ? all.join(',') : undefined;
};

/**
 * Builds a DocumentPicker `type` from the file param's validation schema.
 * Falls back to broad mime wildcards based on the param's `fileType`.
 */
export const buildDocumentPickerType = (param) => {
  const fileVal = param?.validation?.file || {};
  const mimes = Array.isArray(fileVal.acceptedMimeTypes) ? fileVal.acceptedMimeTypes : [];
  if (mimes.length > 0) return mimes;
  switch (fileVal.fileType) {
    case 'image': return 'image/*';
    case 'audio': return 'audio/*';
    case 'video': return 'video/*';
    case 'document': return ['application/pdf', 'text/*', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    default: return '*/*';
  }
};

/**
 * Hook that encapsulates the file-upload flow used by file-typed custom
 * parameters. Handles credit checks, allowed-type prefetch, picker invocation,
 * upload via the file store, and pushing the resulting file into the chat
 * input's attachment list.
 *
 * Callers can override the destination via `onAddFile` / `onAddUploadingFile`.
 * By default, the file is added to the current chat input attachments.
 */
export default function useFileParamUpload({ param, onAddFile, onAddUploadingFile } = {}) {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);

  const { uploadFile, isFileAllowed, fetchAllowedFileTypes, hasCreditsForFileUpload } =
    useFileStore((state) => state.actions);
  const { addToast } = useToastStore((state) => state.actions);
  const addFileToChatInput = useChatInputStore((state) => state.addFile);
  const updateFileInChatInput = useChatInputStore((state) => state.updateFileById);
  // Cross-context removal: the user can switch conversations mid-upload, and a
  // leaked temp placeholder permanently disables Send in the origin context.
  const removeFileFromChatInput = useChatInputStore((state) => state.removeFileByIdFromAllContexts);

  const acceptString = useMemo(() => buildAcceptString(param), [param]);
  const pickerType = useMemo(() => buildDocumentPickerType(param), [param]);
  const fileType = param?.validation?.file?.fileType || 'any';
  const supportsPhotoLibrary = isPhotoLibraryFileType(fileType);
  const supportsCamera = fileType === 'image' || fileType === 'video' || fileType === 'any';

  // The flow is: (1) push a placeholder with a `tempId` so the UI shows an
  // uploading card immediately, (2) await the real upload, (3) swap the
  // placeholder for the uploaded file in place. We had a duplication bug
  // where step 3 just appended a second entry instead of swapping; consumers
  // that supply an `onAddFile(file, tempId)` (e.g. ChatInput's
  // `handleOnAddFile`) handle the swap themselves, otherwise we do it here
  // with `updateFileById` on the chat-input store.
  const handleAddUploadingFile = useCallback(
    (file) => {
      if (onAddUploadingFile) onAddUploadingFile(file);
      else addFileToChatInput(file);
    },
    [onAddUploadingFile, addFileToChatInput]
  );

  const handleAddFile = useCallback(
    (uploaded, tempId) => {
      if (onAddFile) {
        onAddFile(uploaded, tempId);
        return;
      }
      if (tempId) {
        // Swap in-place so the temp uploading card becomes the real file.
        updateFileInChatInput(tempId, { ...uploaded, isUploading: false });
        return;
      }
      addFileToChatInput(uploaded);
    },
    [onAddFile, updateFileInChatInput, addFileToChatInput]
  );

  const processPickedFile = useCallback(
    async (pickedFile) => {
      let tempId = null;
      try {
        const { isAllowed, message } = isFileAllowed(pickedFile);
        if (!isAllowed) {
          addToast(message, 'error');
          return;
        }

        const fileSchema = param?.validation?.file || {};
        const limits = getFileValidationLimits(fileSchema);
        const probedDuration = await probeMediaDurationSeconds(pickedFile);
        const limitError = describeFileLimitViolation(
          {
            size: pickedFile.size,
            duration: probedDuration ?? pickedFile.duration,
            metadata: pickedFile.metadata,
          },
          limits,
        );
        if (limitError) {
          addToast(limitError, 'error');
          return;
        }

        tempId = getTemporaryFileId(pickedFile.name);
        handleAddUploadingFile({
          id: tempId,
          name: pickedFile.name,
          size: pickedFile.size,
          type: pickedFile.type,
          isUploading: true,
          uri: pickedFile.uri,
          previewFile: pickedFile,
        });

        const uploaded = await uploadFile({ file: pickedFile, name: pickedFile.name });
        if (uploaded) {
          handleAddFile(uploaded, tempId);
        } else {
          if (tempId) removeFileFromChatInput(tempId);
          addToast(t('chatInput.uploadNoFileReturned') || 'Upload returned no file.', 'error');
        }
      } catch (err) {
        console.error('[useFileParamUpload] upload failed', err);
        if (tempId) removeFileFromChatInput(tempId);
        addToast(err?.message || t('chatInput.uploadFailed') || 'Failed to upload file.', 'error');
      }
    },
    [addToast, handleAddFile, handleAddUploadingFile, isFileAllowed, param, removeFileFromChatInput, t, uploadFile]
  );

  const ensureCanUpload = useCallback(async () => {
    const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      return false;
    }
    try {
      await fetchAllowedFileTypes();
    } catch (err) {
      console.error('[useFileParamUpload] failed to fetch allowed file types', err);
    }
    return true;
  }, [addToast, fetchAllowedFileTypes, hasCreditsForFileUpload]);

  const pickAndUploadNative = useCallback(async () => {
    if (Platform.OS === 'web' || isUploading) return;
    if (!(await ensureCanUpload())) return;

    setIsUploading(true);
    try {
      const result = await DocumentPicker.getDocumentAsync({
        multiple: false,
        copyToCacheDirectory: true,
        type: pickerType,
      });
      if (result.canceled) return;
      const asset = result.assets?.[0];
      if (!asset) return;
      await processPickedFile({
        uri: asset.uri,
        name: asset.name,
        size: asset.size,
        type: asset.mimeType,
      });
    } finally {
      setIsUploading(false);
    }
  }, [ensureCanUpload, isUploading, pickerType, processPickedFile]);

  const handleMissingPermission = useCallback(
    (permission, kind /* 'camera' | 'photoLibrary' */) => {
      const canAskAgain = permission?.canAskAgain !== false;
      if (kind === 'camera') {
        if (canAskAgain) {
          addToast(t('chatInput.files.cameraPermissionRequired') || 'Camera permission required', 'error');
        } else {
          addToast(t('chatInput.files.cameraDisabled') || 'Camera access is disabled. Opening Settings…', 'error');
          Linking.openSettings().catch((err) => console.warn('[useFileParamUpload] openSettings failed', err));
        }
      } else {
        if (canAskAgain) {
          addToast(t('chatInput.files.photoLibraryPermissionRequired') || 'Photo library permission required', 'error');
        } else {
          addToast(t('chatInput.files.photoLibraryDisabled') || 'Photo library access is disabled. Opening Settings…', 'error');
          Linking.openSettings().catch((err) => console.warn('[useFileParamUpload] openSettings failed', err));
        }
      }
    },
    [addToast, t]
  );

  const guessNameFromUri = (uri, fallback) => {
    if (!uri || typeof uri !== 'string') return fallback;
    const tail = uri.split('/').pop();
    if (tail && tail.includes('.')) return tail;
    return fallback;
  };

  const guessMimeFromAsset = (asset, fallback) => {
    if (asset?.mimeType) return asset.mimeType;
    if (asset?.type === 'video') return 'video/mp4';
    if (asset?.type === 'image') return 'image/jpeg';
    return fallback;
  };

  // Launch the device camera and upload the captured photo/video. Honors
  // the param's `validation.file.fileType` (image → photo, video → video).
  // For ambiguous file types (`any`), defaults to photo capture.
  const pickAndUploadFromCamera = useCallback(async () => {
    if (Platform.OS === 'web' || isUploading) return;
    if (!supportsCamera) return;
    if (!(await ensureCanUpload())) return;

    setIsUploading(true);
    try {
      const permission = await ImagePicker.requestCameraPermissionsAsync();
      if (permission.status !== 'granted') {
        handleMissingPermission(permission, 'camera');
        return;
      }
      const mediaType = fileType === 'video' ? 'videos' : 'images';
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: [mediaType],
        quality: 0.8,
      });
      if (result.canceled) return;
      const asset = result.assets?.[0];
      if (!asset) return;
      const stampedName = mediaType === 'videos'
        ? `camera_video_${Date.now()}.mp4`
        : `camera_photo_${Date.now()}.jpg`;
      const fallbackMime = mediaType === 'videos' ? 'video/mp4' : 'image/jpeg';
      await processPickedFile({
        uri: asset.uri,
        name: asset.fileName || stampedName,
        size: asset.fileSize || 0,
        type: guessMimeFromAsset(asset, fallbackMime),
        duration: asset.duration,
      });
    } catch (err) {
      console.error('[useFileParamUpload] camera capture failed', err);
      addToast(err?.message || t('chatInput.files.takePhotoFailed') || 'Failed to take photo', 'error');
    } finally {
      setIsUploading(false);
    }
  }, [
    addToast, ensureCanUpload, fileType, handleMissingPermission,
    isUploading, processPickedFile, supportsCamera, t,
  ]);

  // Launch the device photo library and upload the chosen media. Filters
  // by the param's `validation.file.fileType` (image / video / both).
  const pickAndUploadFromPhotos = useCallback(async () => {
    if (Platform.OS === 'web' || isUploading) return;
    if (!supportsPhotoLibrary && fileType !== 'any') return;
    if (!(await ensureCanUpload())) return;

    setIsUploading(true);
    try {
      const mediaTypes = fileTypeToImagePickerMediaTypes(fileType);
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes,
        allowsMultipleSelection: false,
        quality: 0.8,
      });
      if (result.canceled) return;
      const asset = result.assets?.[0];
      if (!asset) return;
      const fallbackMime = asset?.type === 'video' ? 'video/mp4' : 'image/jpeg';
      const fallbackName = asset?.type === 'video' ? 'video.mp4' : 'photo.jpg';
      await processPickedFile({
        uri: asset.uri,
        name: asset.fileName || guessNameFromUri(asset.uri, fallbackName),
        size: asset.fileSize || 0,
        type: guessMimeFromAsset(asset, fallbackMime),
        duration: asset.duration,
      });
    } catch (err) {
      console.error('[useFileParamUpload] photo picker failed', err);
      addToast(err?.message || t('chatInput.files.pickFromPhotosFailed') || 'Failed to pick from photos', 'error');
    } finally {
      setIsUploading(false);
    }
  }, [
    addToast, ensureCanUpload, fileType,
    isUploading, processPickedFile, supportsPhotoLibrary, t,
  ]);

  // Source IDs surfaced to consumers (e.g. FileSourceDropdown) so they can
  // render menu items conditioned on platform + file type. Stays close to
  // the existing iOS attachment ActionSheet vocabulary used elsewhere in
  // the composer.
  const nativePickSources = useMemo(() => {
    if (Platform.OS === 'web') return [];
    const sources = [];
    if (supportsCamera) {
      sources.push({
        id: 'camera',
        label: fileType === 'video'
          ? (t('chatInput.files.actionSheetTakeVideo') || 'Take Video')
          : (t('chatInput.files.actionSheetTakePhoto') || 'Take Photo'),
        icon: fileType === 'video' ? 'camera-movie' : 'camera',
        iosIcon: fileType === 'video' ? 'video' : 'camera',
        run: pickAndUploadFromCamera,
      });
    }
    if (supportsPhotoLibrary || fileType === 'any') {
      sources.push({
        id: 'photos',
        label: t('chatInput.files.actionSheetChooseFromPhotos') || 'Choose from Photos',
        icon: 'images',
        iosIcon: 'photo.on.rectangle',
        run: pickAndUploadFromPhotos,
      });
    }
    sources.push({
      id: 'files',
      label: t('chatInput.files.actionSheetBrowseFiles') || 'Browse Files',
      icon: 'arrow-up-from-bracket',
      iosIcon: 'arrow.up.doc',
      run: pickAndUploadNative,
    });
    return sources;
  }, [
    fileType, pickAndUploadFromCamera, pickAndUploadFromPhotos,
    pickAndUploadNative, supportsCamera, supportsPhotoLibrary, t,
  ]);

  /**
   * Web file-input change handler. Pair with a hidden `<input type="file" />`
   * whose `accept` is set to {@link acceptString}. Triggers via `ref.click()`.
   */
  const handleWebFileInputChange = useCallback(
    async (event) => {
      const picked = Array.from(event?.target?.files || []);
      try {
        if (event?.target) event.target.value = '';
      } catch (_) {}
      if (!picked.length) return;
      if (!(await ensureCanUpload())) return;
      setIsUploading(true);
      try {
        for (const file of picked) {
          await processPickedFile(file);
        }
      } finally {
        setIsUploading(false);
      }
    },
    [ensureCanUpload, processPickedFile]
  );

  return {
    isUploading,
    acceptString,
    pickerType,
    pickAndUploadNative,
    pickAndUploadFromCamera,
    pickAndUploadFromPhotos,
    nativePickSources,
    handleWebFileInputChange,
    processPickedFile,
    ensureCanUpload,
  };
}
